/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// @mui material components
import PropTypes from "prop-types";
import moment from "moment";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

// Material Dashboard 2 PRO React examples
import DataTableNotifications from "components/DataTableNotifications";

function CellDate({ value }) {
  return <span>{moment(value).format("DD/MM/YYYY HH:mm")}</span>;
}

function CellReadByClient({ row }) {
  const { t } = useTranslation();
  const isReadByClient = !!row?.original?.readByClient;
  return <span>{isReadByClient ? t("yes") : t("no")}</span>;
}

const columns = () => {
  const { t } = useTranslation();
  return [
    { Header: t("message"), accessor: "message", width: 20 },
    {
      Header: t("readByClient"),
      accessor: "readByClient",
      Cell: CellReadByClient,
      width: 15,
    },
    {
      Header: t("createdAt"),
      accessor: "createdAt",
      Cell: CellDate,
      maxWidth: 100,
    },
  ];
};

function AlertDialog({ open, handleClose, handleAgree }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle id="alert-dialog-title">{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{t("stepNotUndo")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

function NotificationsTable({ notifications, handleEdit, handleRemove }) {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [uids, setUids] = useState([]);
  const [actionType, setActionType] = useState("");
  const [formatedData, setFormatedData] = useState([]);

  const remove = (data) => {
    const notificationsIndexes = [];
    data.forEach((notification) => {
      const {
        original: { uid },
      } = notification;
      notificationsIndexes.push(uid);
    });
    setActionType("remove");
    setUids(notificationsIndexes);
    setOpenConfirm(true);
  };

  const confirm = () => {
    switch (actionType) {
      case "remove": {
        setActionType("");
        setOpenConfirm(false);
        handleRemove(uids);
        break;
      }
      default:
        break;
    }
  };

  const read = (data, readState) => {
    if (Array.isArray(data)) {
      if (!readState) return;

      const newNotifications = {};
      data.forEach((notification) => {
        const {
          original: { uid },
        } = notification;
        newNotifications[uid] = {
          ...notification.original,
          readByMainUser: readState,
        };
      });
      handleEdit(newNotifications);
    }
  };

  useEffect(() => {
    const notificationsArray = Object.values(notifications || {});
    const formattedNotifications = notificationsArray.map((notification) => ({
      ...notification,
      readByClient: !!notification.read,
      read: !!notification.readByMainUser,
    }));
    setFormatedData(formattedNotifications);
  }, [notifications]);

  return (
    <MDBox pt={3} pb={3} pl={3} pr={3}>
      <AlertDialog
        open={openConfirm}
        handleClose={() => {
          setOpenConfirm(false);
        }}
        handleAgree={() => {
          confirm();
        }}
      />
      <DataTableNotifications
        table={{
          columns: columns(),
          rows: formatedData,
        }}
        canSearch
        canSelect
        read={read}
        remove={remove}
        actionIcon="check"
        actionColor="success"
        multipleSelect
      />
    </MDBox>
  );
}

NotificationsTable.defaultProps = {
  notifications: {},
};

NotificationsTable.propTypes = {
  notifications: PropTypes.object,
  handleRemove: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

AlertDialog.defaultProps = {
  handleAgree: () => {},
};

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
};

CellDate.defaultProps = {
  value: 0,
};

CellDate.propTypes = {
  value: PropTypes.any,
};

CellReadByClient.propTypes = {
  row: PropTypes.object.isRequired,
};

export default NotificationsTable;
